import { useEffect, useState } from "react";
import { X, Mail, BadgeCheck, Send } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { lengthLabel, cmToInput } from "@/lib/units";

const PROFILE_KEY = "mp_fit_profile_v1";
const SUBSCRIBED_KEY = "mp_waitlist_subscriptions_v1";

/**
 * WaitlistDialog — visitor "send me when my fit lands" capture.
 *
 * Pre-fills the email + fit profile from localStorage so the visitor can subscribe
 * with one click. Submits to POST /api/waitlist. Records the subscription locally
 * (per item) so the trigger button can flip to "you're on the list" on repeat visits.
 *
 * Props:
 *  - open / onClose
 *  - item: the storefront item (needs id + category + category_name + category_schema + measurements_map)
 *  - unitSystem: "metric" | "imperial"
 *  - onSubscribed(itemId)
 */
export default function WaitlistDialog({ open, onClose, item, unitSystem, onSubscribed }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState({});

  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem(PROFILE_KEY);
      if (raw) setProfile(JSON.parse(raw) || {});
      const lastEmail = localStorage.getItem("mp_waitlist_last_email");
      if (lastEmail) setEmail(lastEmail);
    } catch (e) { /* noop */ }
  }, [open]);

  if (!open || !item) return null;

  const cat = item.category_name?.toLowerCase() || "pieces";

  // Friendly summary of which measurements the matcher will use.
  const lengthFields = (item.category_schema || []).filter((f) => f.unit_kind === "length");
  const overlapKeys = lengthFields.filter((f) => profile[f.key]).map((f) => f.label.toLowerCase());

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !item.category) {
      toast.error("email + category are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/waitlist", {
        email: email.trim().toLowerCase(),
        category_slugs: [item.category],
        fit_profile: profile || {},
        source_item_id: item.id,
      });
      try {
        localStorage.setItem("mp_waitlist_last_email", email.trim().toLowerCase());
        const stored = JSON.parse(localStorage.getItem(SUBSCRIBED_KEY) || "{}");
        stored[item.id] = Date.now();
        localStorage.setItem(SUBSCRIBED_KEY, JSON.stringify(stored));
      } catch (err) { /* noop */ }
      toast.success("you're on the list. we'll write when one lands.");
      if (onSubscribed) onSubscribed(item.id);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "could not subscribe.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-auto"
      onClick={onClose}
      data-testid="waitlist-dialog"
    >
      <form
        className="bg-ivory border border-taupe max-w-md w-full p-8 relative"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <button type="button" onClick={onClose} aria-label="close" className="absolute top-4 right-4 text-taupe hover:text-charcoal" data-testid="waitlist-close">
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <Mail className="w-4 h-4 text-brass" />
          <span className="editorial-label">first-look list</span>
        </div>
        <h2 className="font-serif text-3xl lowercase text-charcoal mt-1">send me when my fit lands.</h2>
        <p className="text-xs text-espresso mt-2">
          we&apos;ll notify you the moment a new {cat} arrives in your size and proportions — before it hits the public shop.
        </p>

        <label className="editorial-label block mt-6">your email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
          placeholder="you@maison.com"
          data-testid="waitlist-email"
          className="w-full bg-transparent border-b border-taupe py-2 mt-1 text-charcoal focus:outline-none focus:border-brass"
        />

        <div className="mt-6 border-t border-taupe/40 pt-4">
          <p className="editorial-label">match against</p>
          <p className="text-sm text-charcoal mt-2 lowercase">{item.category_name || "—"}</p>
          {overlapKeys.length > 0 ? (
            <p className="text-xs text-espresso mt-3" data-testid="waitlist-overlap">
              your saved fit profile · {overlapKeys.join(" · ")}
            </p>
          ) : lengthFields.length > 0 ? (
            <p className="text-xs text-taupe italic mt-3" data-testid="waitlist-no-profile">
              you haven&apos;t saved a fit profile yet — we&apos;ll match by category alone. open the &ldquo;will this fit me?&rdquo; widget on any piece to add measurements.
            </p>
          ) : (
            <p className="text-xs text-taupe italic mt-3">
              we&apos;ll match by category alone (no typed measurements on this kind of piece).
            </p>
          )}
          {profile && Object.keys(profile).length > 0 && lengthFields.length > 0 && (
            <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[0.7rem] text-charcoal" data-testid="waitlist-profile-summary">
              {lengthFields.filter((f) => profile[f.key]).map((f) => (
                <li key={f.key} className="flex justify-between">
                  <span className="lowercase text-taupe">{f.label}</span>
                  <span className="tabular-nums">{cmToInput(profile[f.key], unitSystem)} {lengthLabel(unitSystem)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-xs tracking-[0.25em] uppercase text-taupe hover:text-charcoal px-3 py-2"
            data-testid="waitlist-cancel"
          >
            never mind
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="waitlist-submit"
            className="inline-flex items-center gap-2 bg-brass hover:bg-espresso text-ivory px-5 py-2.5 text-xs tracking-[0.25em] uppercase disabled:opacity-50"
          >
            {submitting ? <span>subscribing…</span> : <><Send className="w-3 h-3" /> add me</>}
          </button>
        </div>

        <p className="text-[0.6rem] tracking-widest uppercase text-taupe mt-4 flex items-center gap-1">
          <BadgeCheck className="w-3 h-3" /> unsubscribe any time. no marketing — only the matching pieces.
        </p>
      </form>
    </div>
  );
}
